import { useId, type ComponentPropsWithRef, type ReactNode } from 'react';
import { cn } from '../lib/cn';
import { disabledStyles, focusRing } from '../lib/styles';

export type ToggleProps = Omit<
    ComponentPropsWithRef<'button'>,
    'onChange' | 'children' | 'type' | 'role' | 'aria-checked'
> & {
    checked: boolean;
    onCheckedChange: (checked: boolean) => void;
    /** Row label. Required — a bare switch says nothing. */
    label: ReactNode;
    /** 12px grey line under the label ("Na 14 en 30 dagen"). */
    description?: ReactNode;
    /**
     * `row` renders the label on the left and the switch on the right, with the whole
     * row as the hit area — the layout used in Instellingen and Link & toegang.
     * `bare` renders only the switch, for when the caller supplies its own row.
     */
    layout?: 'row' | 'bare';
};

/**
 * Switch. Track 42x24 radius 12, knob 18px white.
 * On: `--accent` with the knob at 18px. Off: `--border-control` with the knob at 0.
 *
 * `role="switch"` with `aria-checked`; Space and Enter toggle it because it is a
 * real button.
 */
export function Toggle({
    checked,
    onCheckedChange,
    label,
    description,
    layout = 'row',
    className,
    disabled,
    onClick,
    ...rest
}: ToggleProps) {
    const labelId = useId();

    const control = (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            aria-labelledby={layout === 'row' ? labelId : undefined}
            disabled={disabled}
            onClick={(event) => {
                onClick?.(event);
                if (event.defaultPrevented) return;
                onCheckedChange(!checked);
            }}
            className={cn(
                'inline-flex h-6 w-[42px] shrink-0 cursor-pointer items-center rounded-[12px] p-[3px]',
                'border-0 transition-colors duration-100',
                checked ? 'bg-[var(--accent)]' : 'bg-[var(--border-control)]',
                focusRing,
                disabledStyles,
                layout === 'bare' && className,
            )}
            {...rest}
        >
            <span
                aria-hidden="true"
                className={cn(
                    'block h-[18px] w-[18px] rounded-full bg-[var(--surface-card)] transition-[margin] duration-100',
                    checked ? 'ml-[18px]' : 'ml-0',
                )}
            />
        </button>
    );

    if (layout === 'bare') return control;

    return (
        <div
            className={cn(
                'flex items-center justify-between gap-[14px] py-[11px]',
                'font-[family-name:var(--font-body)] text-[var(--text-primary)]',
                className,
            )}
        >
            <span className="min-w-0">
                <span id={labelId} className="block text-[14px] leading-[1.35] font-medium">
                    {label}
                </span>
                {description ? (
                    <span className="mt-[3px] block text-[12px] leading-[1.4] text-[var(--text-tertiary-safe)]">
                        {description}
                    </span>
                ) : null}
            </span>
            {control}
        </div>
    );
}
